import _extends from '@babel/runtime/helpers/extends';
import React from 'react';
import PropTypes from 'prop-types';
import { p as props } from './componentEl-fcbc301e.js';
import { a as addClassNames, c as classNamePropTypes } from './classNames-1ed461aa.js';
import { a as addSpacingClasses, m as marginPropTypes } from './spacing-0f39ba4a.js';
import { s as setIcon } from './icons-3c94a1f0.js';
import Icon from './Icon.js';

function Button({
  active,
  componentEl: Component,
  componentRef,
  children,
  className,
  color,
  disabled,
  fullWidth,
  fullHeight,
  icon,
  iconPosition,
  margin,
  primary,
  secondary,
  tertiary,
  pill,
  justified,
  size,
  // eslint-disable-next-line react/prop-types
  padding,
  ...otherProps
}) {
  let cssClasses = ['st_btn', 'st_focusable'];
  cssClasses = addClassNames(className, cssClasses);
  cssClasses = addSpacingClasses({
    margin,
    cssClasses
  });
  if (primary || secondary || tertiary) cssClasses.push(`st_btn--size-${size}`);
  let buttonStyleType;
  if (primary) {
    buttonStyleType = 'primary';
  } else if (secondary) {
    buttonStyleType = 'secondary';
  } else if (tertiary) {
    buttonStyleType = 'tertiary';
  } else if (pill) {
    buttonStyleType = 'pill';
  }
  if (buttonStyleType) cssClasses.push(`st_btn--${buttonStyleType}`);
  if (active) cssClasses.push('st_btn--active');
  if (fullWidth) cssClasses.push('st_width--full st_btn--full-width');
  if (fullHeight) cssClasses.push('st_height--full');
  if (disabled) cssClasses.push('st_btn--disabled');
  if (color && buttonStyleType === 'tertiary') cssClasses.push(`st_btn--tertiary--${color}`);
  const componentProps = {
    ...otherProps
  };

  // add back disabled attribute only for button
  if (Component === 'button') {
    componentProps.disabled = disabled;
  }

  // don't add margin to button if there's no other content
  let buttonMargin = size === 'sm' ? 'xs' : 'sm';
  if (icon && !children) {
    buttonMargin = 'none';
  }

  // overwrite justify-content if justification included
  if (justified) {
    if (fullWidth) cssClasses.push(`st_btn--justified-${justified}`);
  }
  const iconProps = {
    className: 'btn__icon'
  };
  const iconArgs = [Icon, icon];
  if (size === 'sm' && buttonStyleType === 'tertiary') iconProps.size = 'smd';
  const leftIcon = setIcon(...iconArgs, {
    margin: {
      right: buttonMargin
    },
    ...iconProps
  });
  const rightIcon = setIcon(...iconArgs, {
    margin: {
      left: buttonMargin
    },
    ...iconProps
  });
  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' '),
    ref: componentRef
  }, componentProps), icon && iconPosition === 'left' && leftIcon, children, icon && iconPosition === 'right' && rightIcon);
}
Button.propTypes = {
  /** whether button is Primary */
  primary: PropTypes.bool,
  /** whether button is Secondary */
  secondary: PropTypes.bool,
  /** whether button is tertiary */
  tertiary: PropTypes.bool,
  /** whether button is pill-shaped */
  pill: PropTypes.bool,
  /** whether button should show as active */
  active: PropTypes.bool,
  /** whether link/button is disabled */
  disabled: PropTypes.bool,
  /** color, but applied only for tertiary */
  color: PropTypes.oneOf(['asphalt', 'blurple', 'emerald', 'gray', 'lava', 'white']),
  /** size of button. Valid for primary, secondary and tertiary only  */
  size: PropTypes.oneOf(['sm', 'md']),
  /** Optional icon. A screentone icon component, e.g. IconStar */
  icon: PropTypes.any,
  /** position of icon relative to text */
  iconPosition: PropTypes.oneOf(['left', 'right']),
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** ref for element */
  componentRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.any
  })]),
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** whether button takes up all available horizontal space */
  fullWidth: PropTypes.bool,
  /** whether button takes up all available vertical space */
  fullHeight: PropTypes.bool,
  /** left/top/right/bottom, and also size */
  margin: marginPropTypes,
  /** justification of button */
  justified: PropTypes.oneOf(['left', 'right', 'center'])
};
Button.defaultProps = {
  active: false,
  componentEl: 'button',
  componentRef: null,
  className: null,
  color: 'blurple',
  disabled: false,
  fullWidth: false,
  fullHeight: false,
  icon: null,
  iconPosition: 'left',
  margin: null,
  primary: false,
  secondary: false,
  tertiary: false,
  pill: false,
  justified: null,
  size: 'md'
};

export { Button as B };
